import { FileUp } from 'lucide-react';
import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { api, getApiErrorMessage } from "@/shared/api/axios";
import { Button } from "@/shared/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/shared/components/ui/Card";
import { Skeleton } from "@/shared/components/ui/Skeleton";

type EntityType = "customer" | "vendor" | "journal" | "budget";
const selectClass =
  "flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm";
const fieldLabels: Record<EntityType, string[]> = {
  customer: [
    "name",
    "email",
    "phone",
    "gstNumber",
    "panNumber",
    "creditLimit",
    "paymentTerms",
    "currency",
  ],
  vendor: [
    "name",
    "email",
    "phone",
    "gstNumber",
    "panNumber",
    "paymentTerms",
    "currency",
  ],
  journal: [
    "group",
    "date",
    "description",
    "reference",
    "accountCode",
    "lineDescription",
    "debit",
    "credit",
  ],
  budget: [
    "group",
    "name",
    "department",
    "financialYear",
    "month",
    "budgetType",
    "accountCode",
    "budgetAmount",
    "alertThreshold",
  ],
};

export function parseAccountsCsv(text: string, delimiter = ",") {
  const rows: string[][] = [];
  let row: string[] = [];
  let value = "";
  let quoted = false;
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    if (char === '"') {
      if (quoted && text[index + 1] === '"') {
        value += '"';
        index += 1;
      } else quoted = !quoted;
    } else if (char === delimiter && !quoted) {
      row.push(value);
      value = "";
    } else if ((char === "\n" || char === "\r") && !quoted) {
      if (char === "\r" && text[index + 1] === "\n") index += 1;
      row.push(value);
      value = "";
      if (row.some((cell) => cell.trim())) rows.push(row);
      row = [];
    } else value += char;
  }
  row.push(value);
  if (row.some((cell) => cell.trim())) rows.push(row);
  if (quoted) throw new Error("CSV contains an unclosed quoted value.");
  if (rows.length < 2)
    throw new Error("CSV needs a header and at least one data row.");
  const headers = rows[0].map((cell, index) =>
    (index === 0 ? cell.replace(/^\uFEFF/, "") : cell).trim(),
  );
  if (
    new Set(headers).size !== headers.length ||
    headers.some((header) => !header)
  )
    throw new Error("CSV headers must be present and unique.");
  return {
    headers,
    rows: rows
      .slice(1)
      .map((cells) =>
        Object.fromEntries(
          headers.map((header, index) => [header, cells[index] ?? ""]),
        ),
      ),
  };
}

export function DataImportsPage() {
  const [entityType, setEntityType] = useState<EntityType>("customer");
  const [fileName, setFileName] = useState("");
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [duplicatePolicy, setDuplicatePolicy] = useState("skip");
  const [preview, setPreview] = useState<any | null>(null);
  const jobs = useQuery({
    queryKey: ["accounts", "imports", "jobs"],
    queryFn: async () =>
      (await api.get<any[]>("/api/accounts/imports/jobs")).data,
  });
  const payload = () => ({
    entityType,
    fileName,
    rows,
    mapping,
    duplicatePolicy,
  });
  const previewMutation = useMutation({
    mutationFn: async () =>
      (await api.post("/api/accounts/imports/preview", payload())).data,
    onSuccess: setPreview,
    onError: (error) =>
      toast.error(getApiErrorMessage(error, "Import preview failed.")),
  });
  const commitMutation = useMutation({
    mutationFn: async () =>
      (
        await api.post("/api/accounts/imports/commit", payload(), {
          headers: { "X-Idempotency-Key": crypto.randomUUID() },
        })
      ).data,
    onSuccess: (job) => {
      toast.success(
        `Import completed: ${job.results.filter((row: any) => ["created", "updated"].includes(row.status)).length} changed.`,
      );
      setPreview(null);
      jobs.refetch();
    },
    onError: (error) =>
      toast.error(getApiErrorMessage(error, "Import failed.")),
  });
  const mappedTargets = useMemo(
    () => new Set(Object.values(mapping).filter(Boolean)),
    [mapping],
  );
  const loadFile = async (file?: File) => {
    if (!file) return;
    try {
      const parsed = parseAccountsCsv(await file.text());
      const allowed = fieldLabels[entityType];
      const auto = Object.fromEntries(
        parsed.headers.map((header) => {
          const normalized = header.toLowerCase().replace(/[^a-z0-9]/g, "");
          const target =
            allowed.find((field) => field.toLowerCase() === normalized) || "";
          return [header, target];
        }),
      );
      setFileName(file.name);
      setHeaders(parsed.headers);
      setRows(parsed.rows);
      setMapping(auto);
      setPreview(null);
    } catch (error: any) {
      toast.error(error.message || "Could not parse CSV.");
    }
  };
  const downloadSample = () => {
    const content = `${fieldLabels[entityType].join(",")}\n${entityType === "journal" ? "J-001,2026-07-22,Opening adjustment,REF-1,5070,Expense,1000,0\nJ-001,2026-07-22,Opening adjustment,REF-1,1020,Bank,0,1000" : entityType === "budget" ? "B-001,FY 2026 Marketing,Marketing,2026-27,2026-04,monthly,5070,75000,90\nB-001,FY 2026 Marketing,Marketing,2026-27,2026-04,monthly,5080,25000,90" : entityType === "customer" ? "Acme Pvt Ltd,accounts@acme.test,919999999999,27ABCDE1234F1Z5,ABCDE1234F,50000,Net 30,INR" : "Supply Co,vendor@example.test,919888888888,27ABCDE1234F1Z5,ABCDE1234F,Net 30,INR"}`;
    const url = URL.createObjectURL(new Blob([content], { type: "text/csv" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${entityType}-import-sample.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="space-y-5 pb-16">
      <PageHeader icon={FileUp} eyebrow="Finance" title="Accounting Data Import" description={<>Map CSV headers, validate every row, choose duplicate handling, and
          commit an idempotent job with durable row-level results.</>} />
      <Card>
        <CardHeader
          title="1. Choose data and file"
          action={
            <Button variant="outline" size="sm" onClick={downloadSample}>
              Download Sample
            </Button>
          }
        />
        <CardBody className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <Field label="Import type">
              <select
                className={selectClass}
                value={entityType}
                onChange={(event) => {
                  setEntityType(event.target.value as EntityType);
                  setHeaders([]);
                  setRows([]);
                  setPreview(null);
                }}
              >
                <option value="customer">Customers</option>
                <option value="vendor">Vendors</option>
                <option value="journal">Manual Journals</option>
                <option value="budget">Budgets</option>
              </select>
            </Field>
            <Field label="Duplicate policy">
              <select
                className={selectClass}
                value={duplicatePolicy}
                onChange={(event) => setDuplicatePolicy(event.target.value)}
              >
                <option value="skip">Skip duplicates</option>
                <option value="update">Update duplicates</option>
                <option value="error">Treat duplicates as errors</option>
              </select>
            </Field>
            <Field label="CSV file">
              <input
                className={selectClass}
                type="file"
                accept=".csv,text/csv"
                onChange={(event) => loadFile(event.target.files?.[0])}
              />
            </Field>
          </div>
          <p className="text-xs text-muted-foreground">
            {fileName
              ? `${fileName}: ${rows.length} row(s)`
              : "UTF-8 CSV with quoted values is supported. Journal and budget rows sharing the same group are validated and created together."}
          </p>
        </CardBody>
      </Card>
      {!!headers.length && (
        <Card>
          <CardHeader
            title="2. Map headers"
            subtitle="Each destination can be mapped once."
          />
          <CardBody>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
              {headers.map((header) => (
                <Field key={header} label={header}>
                  <select
                    className={selectClass}
                    value={mapping[header] || ""}
                    onChange={(event) => {
                      setMapping({ ...mapping, [header]: event.target.value });
                      setPreview(null);
                    }}
                  >
                    <option value="">Ignore</option>
                    {fieldLabels[entityType].map((field) => (
                      <option
                        key={field}
                        value={field}
                        disabled={
                          mappedTargets.has(field) && mapping[header] !== field
                        }
                      >
                        {field}
                      </option>
                    ))}
                  </select>
                </Field>
              ))}
            </div>
            <div className="mt-4 flex justify-end">
              <Button
                onClick={() => previewMutation.mutate()}
                disabled={
                  !rows.length ||
                  (!mappedTargets.has("name") &&
                    entityType !== "journal" &&
                    entityType !== "budget") ||
                  (entityType === "journal" &&
                    (!mappedTargets.has("group") ||
                      !mappedTargets.has("accountCode"))) ||
                  (entityType === "budget" &&
                    (!mappedTargets.has("group") ||
                      !mappedTargets.has("name") ||
                      !mappedTargets.has("accountCode") ||
                      !mappedTargets.has("budgetAmount"))) ||
                  previewMutation.isPending
                }
              >
                {previewMutation.isPending ? "Validating..." : "Preview Import"}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}
      {preview && (
        <Card>
          <CardHeader
            title="3. Validation result"
            subtitle={`${preview.eligible} eligible; ${preview.invalid} invalid. Invalid rows/groups will not be committed.`}
            action={
              <Button
                onClick={() => commitMutation.mutate()}
                disabled={!preview.eligible || commitMutation.isPending}
              >
                {commitMutation.isPending
                  ? "Importing..."
                  : "Commit Eligible Records"}
              </Button>
            }
          />
          <CardBody className="p-0">
            <div className="max-h-[430px] overflow-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted/95 text-left text-xs uppercase">
                  <tr>
                    <th className="px-4 py-3">Row / group</th>
                    <th className="px-4 py-3">Action</th>
                    <th className="px-4 py-3">Name / description</th>
                    <th className="px-4 py-3">Validation</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.records.map((record: any) => (
                    <tr
                      key={`${record.rowNumber}-${record.group}`}
                      className="border-t"
                    >
                      <td className="px-4 py-3">
                        {record.group || `Row ${record.rowNumber}`}
                      </td>
                      <td className="px-4 py-3 capitalize">{record.action}</td>
                      <td className="px-4 py-3">
                        {record.normalized.name ||
                          record.normalized.description ||
                          "—"}
                      </td>
                      <td
                        className={`px-4 py-3 ${record.eligible ? "text-emerald-600" : "text-destructive"}`}
                      >
                        {record.eligible ? "Ready" : record.reason}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      )}
      <Card>
        <CardHeader title="Import history" />
        <CardBody className="p-0">
          {jobs.isLoading ? (
            <Skeleton className="h-32" />
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase">
                <tr>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">File</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Rows</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {jobs.data?.map((job) => (
                  <tr key={job._id} className="border-t">
                    <td className="px-4 py-3">
                      {new Date(job.createdAt).toLocaleString("en-IN")}
                    </td>
                    <td className="px-4 py-3">
                      {job.fileName || "API import"}
                    </td>
                    <td className="px-4 py-3 capitalize">{job.entityType}</td>
                    <td className="px-4 py-3">{job.rowCount}</td>
                    <td className="px-4 py-3 capitalize">
                      {job.status.replaceAll("_", " ")}
                    </td>
                  </tr>
                ))}
                {!jobs.data?.length && (
                  <tr>
                    <td
                      colSpan={5}
                      className="p-8 text-center text-muted-foreground"
                    >
                      No import jobs yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="space-y-2 text-sm">
      <span className="font-medium">{label}</span>
      {children}
    </label>
  );
}
